/**
 * Car Virtual PV event data builder
 * Araç kiralama sayfaları için virtual_pv event payload'ı oluşturur
 * Karşılığı olmayan alanlar boş string olarak gönderilir
 */

import { cleanUndefined } from "@/analytics/services/car/utils";
import appConfig from "@/configs/app-config";
import { SAKA_COOKIE_NAMES } from "@/constants/cookieNames";
import { normalizePhoneForHash, sha256Hex } from "@/utils/cryptoUtils";

/**
 * Session'dan user bilgilerini çıkarır
 * @param {Object} session - Server veya client session
 * @param {boolean} isLogin - Kullanıcı giriş yapmış mı
 * @param {boolean} cookies - guest user bilgisi için cookie methodu (client side ise js-cookie, server side ise next cookies() vb.)
 * @returns {Object} User fields for virtual_pv
 */
export async function getCarVirtualPvUserFields(session, isLogin, cookies) {
  const hashedFields = getGuestUserHashedFields(cookies);

  const memberEmail = isLogin ? (session?.Email ?? null) : null;
  const memberPhone =
    isLogin && session?.MobilePhoneNumberPrefix && session?.MobilePhoneNumber
      ? `${session.MobilePhoneNumberPrefix}${session.MobilePhoneNumber}`
      : null;

  const [user_hashed_email, user_hashed_phone] = await Promise.all([
    memberEmail ? sha256Hex(memberEmail) : undefined,
    memberPhone ? sha256Hex(normalizePhoneForHash(memberPhone)) : undefined,
  ]);

  const deviceIdCookie = cookies.get(SAKA_COOKIE_NAMES.DEVICE_ID);
  const deviceId = deviceIdCookie?.value || deviceIdCookie || null;

  return {
    turna_session_id: session?.SessionId ?? null,
    turna_device_id: deviceId,
    version: appConfig.WEBSITE_VERSION,
    user_id:
      isLogin && session?.UserId != null ? String(session.UserId) : "",
    user_hashed_email: user_hashed_email ?? null,
    user_hashed_phone: user_hashed_phone ?? null,
    user_promotions_consent: session?.IsMailingAllowed || false,
    user_type: isLogin ? "member" : "guest",
    guest_user_hashed_email: !isLogin
      ? (session?.Email ? session.Email : null) ||
        hashedFields?.hashedEmail ||
        null
      : null,
    guest_user_hashed_phone: !isLogin
      ? (session?.Phone ? session.Phone : null) ||
        hashedFields?.hashedPhone ||
        null
      : null,
    // user_mail: memberEmail,
    // user_phone: memberPhone,
    first_time_purchaser: null,
    user_turna_points:
      typeof session?.UserPointTotal !== "undefined"
        ? session?.UserPointTotal?.TotalUsablePoint
        : 0,
  };
}

export function getCarVirtualPvProductFields(product) {
  return {
    drop_off_location_name: product?.dropOffLocation || null,
    drop_off_city_name: product?.dropOffCity || null,
    pick_up_location_name: product?.pickUpLocation || null,
    pick_up_city_name: product?.pickUpCity || null,
    location_type: product?.locationType || null,
    provider: product.provider || null,
    vehicle_class: product?.vehicleClass || null,
    fuel_type: product?.fuelType || null,
    vehicle_brand: product?.vehicleBrand || null,
  };
}

/**
 * Car virtual_pv için base data oluşturur (landing sayfaları, ana sayfa vb.)
 * @param {Object} params
 * @param {Object} params.session - Server veya client session
 * @param {string} params.locale - Dil kodu (tr, en)
 * @param {string} params.pageType - dataLayer page_type
 */
export async function buildCarVirtualPvData({
  session,
  locale,
  pageType,
  cookies,
  data = {},
}) {
  const isLogin = session?.IsLoggedIn === true || !!session?.User;
  const userFields = await getCarVirtualPvUserFields(session, isLogin, cookies);
  const productData = getCarVirtualPvProductFields(data);

  return cleanUndefined({
    page_type: pageType,
    language: locale ? locale.toUpperCase() : "TR",
    virtual_page_url: data?.pathname,
    website_version: appConfig.WEBSITE_VERSION,
    digital_platform_country: appConfig.COUNTRY_CODE,
    ...userFields,
    ...productData,
  });
}

const getGuestUserHashedFields = (cookies) => {
  const hashedContent = cookies.get(SAKA_COOKIE_NAMES.GUEST_USER);
  const content = hashedContent?.value || hashedContent || "";

  if (!content) {
    return null;
  }

  const [hashedEmail, hashedPhone] = content.split(":");

  return {
    hashedEmail,
    hashedPhone,
  };
};
